import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOperator } from "@/context/OperatorContext";
import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { brandPath } from "@/lib/brand";

export default function SsoLaunch() {
  const { launch, apiError } = useOperator();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // single-use: never post the same lt twice
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const p = (x) => brandPath(x); // read the brand before the address bar is cleaned
    const launchPath = p("/sso/launch");
    const lt = params.get("lt");
    // strip the one-time pass from the address bar immediately
    window.history.replaceState({}, "", launchPath);

    if (!lt) { navigate(p("/login"), { replace: true }); return; }

    (async () => {
      try {
        const res = await launch(lt);
        if (res?.redirected) return; // no access -> gateway redirect handled in context
        if (res.role === "superadmin") navigate(p("/admin"), { replace: true });
        else if (res.role === "tenant") navigate(p("/dashboard"), { replace: true });
        else setError("Access confirmed, but no store is assigned to your account yet. Contact the platform owner.");
      } catch (e) {
        setError(apiError(e.response?.data?.detail) || e.message || "We couldn't sign you in automatically.");
      }
    })();
  }, [launch, apiError, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background text-foreground px-6 text-center">
        <AlertTriangle className="h-6 w-6 text-destructive" />
        <p className="text-sm text-muted-foreground max-w-sm" data-testid="sso-launch-error">{error}</p>
        <Button className="rounded-none" data-testid="sso-launch-login-btn" onClick={() => navigate(brandPath("/login"))}>
          Go to sign in
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background text-foreground">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground" data-testid="sso-launch-msg">Signing you in…</p>
    </div>
  );
}
